import { useEffect, useRef, useState, type ReactNode } from "react";
import { AnimatePresence, motion } from "motion/react";
import { useTranslation } from "react-i18next";
import { ArrowRight } from "lucide-react";

import { Button } from "@/components/ui/button";
import { ButtonTip } from "@/components/ui/button-tip";
import { cn } from "@/lib/utils";
import { EASE, EASE_ARRAY, GrowBox, usePrefersReducedMotion } from "./calm";
import HairlineFigure, { type FigureName } from "./HairlineFigure";

export type PanelKey = "company" | "properties" | "buildings" | "assets" | "spaces" | "floorplans" | "escapePlans" | "optional" | "questions";

/** Which figure explains which step. The questions reuse the asset; the phone step and the review have no panel. */
export const FIGURE_FOR: Record<PanelKey, FigureName> = {
  company: "brand",
  properties: "house",
  buildings: "building",
  assets: "asset",
  spaces: "spaces",
  floorplans: "plan",
  escapePlans: "escape",
  optional: "upkeep",
  questions: "asset",
};

/** A step's heading: the one thing the step is about. Focus moves here when the step changes. */
export function StepHeading({
  eyebrow,
  title,
  lead,
  focus,
  extra,
  centered,
}: {
  eyebrow?: ReactNode;
  title: string;
  lead?: string;
  focus?: boolean;
  extra?: ReactNode;
  centered?: boolean;
}) {
  const ref = useRef<HTMLHeadingElement>(null);
  useEffect(() => {
    if (focus) ref.current?.focus({ preventScroll: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return (
    <header className={cn("space-y-2", centered && "text-center")}>
      {eyebrow && <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{eyebrow}</p>}
      <h1 ref={ref} tabIndex={-1} data-step-heading className="text-3xl font-semibold leading-tight tracking-tight outline-none sm:text-[2.1rem]">
        {title}
      </h1>
      {lead && <p className={cn("max-w-prose text-base leading-7 text-muted-foreground", centered && "mx-auto")}>{lead}</p>}
      {extra}
    </header>
  );
}

/** One main button, "Zurück" quiet beside it, and room for a small text link ("Habe ich gerade nicht"). */
export function StepFooter({
  onBack,
  onNext,
  nextLabel,
  nextDisabled,
  nextIcon = true,
  hint,
  secondary,
  nextTip,
  nextReason,
}: {
  onBack?: () => void;
  onNext: () => void;
  nextLabel?: ReactNode;
  nextDisabled?: boolean;
  nextIcon?: boolean;
  hint?: string | null;
  secondary?: ReactNode;
  /** What the main button does, in one sentence (default: on to the next step). */
  nextTip?: string;
  /** Why the main button is off right now (said in its tip); the reason comes from the very condition that turns it off. */
  nextReason?: string | null;
}) {
  const { t } = useTranslation();
  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-2 px-6 py-4 pb-[max(1rem,env(safe-area-inset-bottom))] sm:px-8">
      {onBack && (
        <ButtonTip label={t("onboardingPage.tips.back")}>
          <Button type="button" variant="ghost" size="sm" onClick={onBack} className="text-muted-foreground">
            {t("onboardingPage.nav.back")}
          </Button>
        </ButtonTip>
      )}
      <div className="ml-auto flex flex-wrap items-center justify-end gap-x-4 gap-y-2">
        {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
        {secondary}
        <ButtonTip label={nextDisabled ? (nextReason ?? "") : (nextTip ?? t("onboardingPage.tips.next"))} disabled={!!nextDisabled}>
          <Button
            type="button"
            size="lg"
            data-primary
            onClick={onNext}
            disabled={nextDisabled}
            className="min-w-32 transition-[opacity,background-color,transform] duration-300 active:scale-[0.99]"
          >
            {nextLabel ?? t("onboardingPage.nav.next")}
            {nextIcon && <ArrowRight className="ml-2 h-4 w-4" aria-hidden="true" />}
          </Button>
        </ButtonTip>
      </div>
    </div>
  );
}

/**
 * The right-hand side of every step: "Was wir damit machen". A hairline figure that shows what becomes
 * of the data (it loops: tour, result for a few seconds, again), three short lines - you provide, we do,
 * you get - and one sentence. All of it is an example, none of it is the customer's data.
 */
export function ExplainPanel({ panel }: { panel: PanelKey }) {
  const { t } = useTranslation();
  const reduced = usePrefersReducedMotion();
  const name = FIGURE_FOR[panel];
  // the figure's own beat (input, processing, result) lights the matching line; its read-out is the caption
  const [beat, setBeat] = useState<number | null>(null);
  const [caption, setCaption] = useState("");
  useEffect(() => {
    setBeat(null);
    setCaption("");
  }, [panel]);

  const lines: [string, string, string][] = [
    ["give", t("onboardingPage.panel.give"), t(`onboardingPage.panel.steps.${panel}.give`)],
    ["make", t("onboardingPage.panel.make"), t(`onboardingPage.panel.steps.${panel}.make`)],
    ["get", t("onboardingPage.panel.get"), t(`onboardingPage.panel.steps.${panel}.get`)],
  ];

  return (
    <aside
      aria-label={t("onboardingPage.panel.title")}
      className="onb-lit relative flex flex-col border-border/70 bg-muted/25 px-5 py-4 lg:border-l lg:px-6 lg:py-6 max-lg:rounded-t-3xl"
      data-testid="explain-panel"
      data-panel={panel}
    >
      <div className="flex items-center justify-between gap-3">
        <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{t("onboardingPage.panel.title")}</p>
      </div>

      <div className="relative mx-auto mt-1 w-full max-w-[360px] max-lg:max-w-[250px]">
        <AnimatePresence mode="popLayout" initial={false}>
          <motion.div
            key={name}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: reduced ? 0.15 : 0.45, ease: EASE_ARRAY }}
          >
            <HairlineFigure
              name={name}
              cycle={panel}
              onBeat={setBeat}
              onRead={setCaption}
            />
          </motion.div>
        </AnimatePresence>
      </div>
      <p className="mt-1 min-h-[2.5rem] text-center text-sm text-muted-foreground" aria-live="polite" data-testid="figure-caption">
        {caption}
      </p>

      <dl className="mt-3 space-y-2.5 max-lg:hidden">
        {lines.map(([key, label, text], index) => (
          <div
            key={key}
            data-beat={index}
            data-active={beat === index ? "true" : "false"}
            className={cn("grid grid-cols-[5.5rem_1fr] items-baseline gap-2 rounded-lg px-2 py-1 text-sm transition-[background-color,opacity] duration-500", beat === index ? "bg-card shadow-sm" : beat === null ? "" : "opacity-60")}
          >
            <dt className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{label}</dt>
            <dd className="min-w-0 leading-5 [overflow-wrap:anywhere]">
              {text}
              {index < 2 && <span aria-hidden="true" className="sr-only"> →</span>}
            </dd>
          </div>
        ))}
      </dl>
      <p className="mt-4 text-sm leading-6 text-muted-foreground max-lg:hidden">{t(`onboardingPage.panel.steps.${panel}.text`)}</p>
      <p className="mt-2 text-sm leading-5 text-muted-foreground lg:hidden">{t(`onboardingPage.panel.steps.${panel}.make`)}</p>
    </aside>
  );
}

/** How long the panel's column takes to fold away or open (the card moves with it). */
export const PANEL_MS = 420;
/** The card's widest form: with the panel at its side, and alone. */
export const CARD_WIDE = 1120;
export const CARD_ALONE = 620;

/**
 * The right-hand column. It is there while a step has a panel; when the next step has none the panel fades out (200 ms)
 * and the column folds shut (`PANEL_MS`), and only then is the panel taken away - so no figure keeps running behind the
 * scenes. The panel inside keeps its own width while the column narrows: its texts do not re-wrap on the way. Under
 * reduced motion nothing moves, the panel is just gone (or there). On a narrow screen the panel stands above the task
 * and simply comes or goes.
 */
function PanelSlot({ panel }: { panel: PanelKey | null }) {
  const reduced = usePrefersReducedMotion();
  const [shown, setShown] = useState<PanelKey | null>(panel);
  useEffect(() => {
    if (panel) {
      setShown(panel);
      return;
    }
    const timer = window.setTimeout(() => setShown(null), reduced ? 0 : PANEL_MS);
    return () => window.clearTimeout(timer);
  }, [panel, reduced]);
  const open = !!panel;
  const keep = panel ?? shown;
  if (!keep) return null;
  const move = reduced ? "" : `transition-[width,opacity] ease-[cubic-bezier(0.22,1,0.36,1)]`;
  return (
    <div
      className={cn("order-first flex min-w-0 flex-col overflow-hidden lg:order-last lg:flex-none", move, open ? "lg:w-[400px]" : "lg:w-0 max-lg:hidden")}
      style={reduced ? undefined : { transitionDuration: `${PANEL_MS}ms` }}
      data-testid="panel-slot"
      data-open={open ? "true" : "false"}
    >
      <div
        className={cn("flex min-w-0 flex-1 flex-col lg:w-[400px] [&>aside]:flex-1", !reduced && "transition-opacity ease-out", open ? "opacity-100" : "opacity-0")}
        style={reduced ? undefined : { transitionDuration: "200ms" }}
      >
        <ExplainPanel panel={keep} />
      </div>
    </div>
  );
}

/**
 * The card every step lives in: always the same size. The task is on the left (it scrolls inside its
 * own column, the footer stays put), the explanation on the right; on a narrow screen the panel moves
 * above the task, smaller. A step without a panel (the phone check, the review) is the card alone, in the
 * middle, in a comfortable width; the card widens and narrows with the panel.
 */
export function StepCard({ panel, children, className }: { panel: PanelKey | null; children: ReactNode; className?: string }) {
  const reduced = usePrefersReducedMotion();
  return (
    <div
      className={cn("relative mx-auto w-full rounded-3xl border border-border bg-card shadow-sm", className)}
      style={{ maxWidth: panel ? CARD_WIDE : CARD_ALONE, ...(reduced ? {} : { transition: `max-width ${PANEL_MS}ms ${EASE}` }) }}
      data-testid="step-card"
      data-panel-open={panel ? "true" : "false"}
    >
      <GrowBox>
        <div className="onb-card flex flex-col lg:flex-row">
          <div className="order-2 flex min-w-0 flex-col lg:order-first lg:flex-1">{children}</div>
          <PanelSlot panel={panel} />
        </div>
      </GrowBox>
    </div>
  );
}

/** The body of the left column: it takes the room its content needs. */
export function StepBody({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={cn("min-w-0 flex-1 space-y-5 px-6 pb-4 pt-6 sm:px-8 sm:pt-8", className)}>{children}</div>;
}
